import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchHtml5Categories } from "../api/html5GamesApi";

// Category type from API
interface Category {
  _id: string;
  name: string;
}

// Props
interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
  setSidebarOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

const toSlug = (str = "") =>
  str.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const Sidebar: React.FC<SidebarProps> = ({
  isOpen = false,
  onClose = () => { },
  setSidebarOpen,
}) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const navigate = useNavigate();
  const { slug } = useParams();

  useEffect(() => {
    const getCategories = async () => {
      try {
        const res = await fetchHtml5Categories({ skip: 0, limit: 100 });
        setCategories(res?.list || []);
      } catch (err) {
        console.error("Failed to load categories", err);
      }
    };
    getCategories();
  }, []);

  return (
    <>
      {/* ☰ Toggle Button for mobile */}
      {!isOpen && setSidebarOpen && (
        <button
          onClick={() => setSidebarOpen(true)}
          className="
    fixed z-[999]
    top-[290px] sm:top-[160px] md:top-[180px] lg:top-[200px] xl:top-[220px]
    left-[4px] sm:left-[6px] md:left-[10px] lg:left-[12px]
    p-2 sm:p-3
    rounded shadow
    bg-[#f92c2c] text-white
    lg:hidden
  "
          aria-label="Open sidebar"
        >
          ☰
        </button>

      )}

      {/* 🔴 Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[998] lg:hidden"
          onClick={onClose}
        ></div>
      )}

      {/* 🔴 Sidebar Container */}
      <div
        className={`fixed top-0 left-0 z-[999] w-[260px] min-h-full
        bg-[#070B28] text-white
        border-r border-white/10
        rounded-tr-3xl rounded-br-3xl
        shadow-2xl p-4
        transition-transform duration-300 ease-in-out
        ${isOpen ? "translate-x-0" : "-translate-x-full"}

        lg:static lg:z-auto lg:translate-x-0 lg:pointer-events-auto
        lg:rounded-none lg:w-60 lg:min-h-full
        lg:bg-[#070B28] lg:border-r lg:border-white/10
        lg:shadow-xl lg:p-4`}
      >
        {/* ✕ Close on Mobile */}
        <div className="flex justify-end lg:hidden">
          <button
            onClick={onClose}
            className="text-white text-2xl px-2 py-1"
          >
            ✕
          </button>
        </div>

        {/* Sidebar Items */}
        <div className="flex flex-col space-y-3 mt-4">
          {categories.map((cat) => {
            const catSlug = toSlug(cat.name);
            const isActive = slug === catSlug;

            return (
              <button
                key={cat._id}
                onClick={() => {
                  navigate(`/category/${catSlug}`);
                  onClose();
                }}
                className={`group flex items-center space-x-3 py-2 px-3 rounded-md text-left transition-all duration-300 ${isActive
                    ? "bg-white text-[#070b28]"
                    : "hover:bg-white/10 text-white"
                  }`}
              >
                <span className="text-xl">🎮</span>
                <span className="text-sm font-semibold">
                  {cat.name}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default Sidebar;
